import { useState, useEffect } from 'react'
import {
  Box,
  Collapse,
  Flex,
  HStack,
  Icon,
  Text,
  Tooltip,
  VStack,
  useColorMode,
} from '@chakra-ui/react'
import { ChevronDown, ChevronRight, Flame, Info, Trash2 } from 'lucide-react'
import { ShareSummary } from '@/lib/api/keys'

interface ArchiveSectionsProps {
  shares: ShareSummary[]
  handleViewDetails: (share: ShareSummary) => void
  getDisplayName: (share: ShareSummary) => string
}

export const ArchiveSections = ({
  shares,
  handleViewDetails,
  getDisplayName,
}: ArchiveSectionsProps) => {
  const { colorMode } = useColorMode()

  const consumedShares = shares.filter((s) => s.status === 'consumed')
  const deletedShares = shares.filter((s) => s.status === 'revoked')

  const [consumedCollapsed, setConsumedCollapsed] = useState(
    () => consumedShares.length > 2
  )
  const [deletedCollapsed, setDeletedCollapsed] = useState(
    () => deletedShares.length > 2
  )

  useEffect(() => {
    setConsumedCollapsed(consumedShares.length > 2)
  }, [consumedShares.length])

  useEffect(() => {
    setDeletedCollapsed(deletedShares.length > 2)
  }, [deletedShares.length])

  if (consumedShares.length === 0 && deletedShares.length === 0) {
    return null
  }

  return (
    <>
      {consumedShares.length > 0 && (
        <Box
          borderTopWidth="1px"
          borderColor={colorMode === 'dark' ? 'stone.700' : 'stone.200'}
        >
          <Flex
            align="center"
            px={3}
            py={2}
            cursor="pointer"
            onClick={() => setConsumedCollapsed(!consumedCollapsed)}
            _hover={{
              bg: colorMode === 'dark' ? 'stone.700' : 'stone.100',
            }}
          >
            <Icon
              as={consumedCollapsed ? ChevronRight : ChevronDown}
              boxSize={3}
              color="orange.500"
              mr={1}
            />
            <Text
              fontSize="xs"
              fontWeight="semibold"
              textTransform="uppercase"
              color="orange.500"
              flex={1}
            >
              Recently Consumed ({consumedShares.length})
            </Text>
            <Tooltip label="Consumed shares will be permanently deleted soon">
              <Icon as={Info} boxSize={3} color="orange.500" />
            </Tooltip>
          </Flex>
          <Collapse in={!consumedCollapsed} animateOpacity>
            <VStack spacing={0} align="stretch" px={2} pb={2}>
              {consumedShares.map((share) => (
                <HStack
                  key={share.id}
                  px={2}
                  py={1.5}
                  borderRadius="md"
                  cursor="pointer"
                  onClick={() => handleViewDetails(share)}
                  opacity={0.6}
                  _hover={{
                    bg: colorMode === 'dark' ? 'stone.700' : 'stone.200',
                    opacity: 0.8,
                  }}
                  transition="all 0.15s ease"
                >
                  <Tooltip label="Consumed (burn after read)">
                    <Icon
                      as={Flame}
                      boxSize={4}
                      color="orange.400"
                      flexShrink={0}
                    />
                  </Tooltip>
                  <Text
                    flex={1}
                    fontSize="sm"
                    noOfLines={1}
                    color={colorMode === 'dark' ? 'stone.400' : 'stone.500'}
                  >
                    {getDisplayName(share)}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Collapse>
        </Box>
      )}

      {deletedShares.length > 0 && (
        <Box
          borderTopWidth="1px"
          borderColor={colorMode === 'dark' ? 'stone.700' : 'stone.200'}
        >
          <Flex
            align="center"
            px={3}
            py={2}
            cursor="pointer"
            onClick={() => setDeletedCollapsed(!deletedCollapsed)}
            _hover={{
              bg: colorMode === 'dark' ? 'stone.700' : 'stone.100',
            }}
          >
            <Icon
              as={deletedCollapsed ? ChevronRight : ChevronDown}
              boxSize={3}
              color="red.500"
              mr={1}
            />
            <Text
              fontSize="xs"
              fontWeight="semibold"
              textTransform="uppercase"
              color="red.500"
              flex={1}
            >
              Recently Deleted ({deletedShares.length})
            </Text>
            <Tooltip label="Recently deleted shares will be permanently deleted soon">
              <Icon as={Info} boxSize={3} color="red.500" />
            </Tooltip>
          </Flex>
          <Collapse in={!deletedCollapsed} animateOpacity>
            <VStack spacing={0} align="stretch" px={2} pb={2}>
              {deletedShares.map((share) => (
                <HStack
                  key={share.id}
                  px={2}
                  py={1.5}
                  borderRadius="md"
                  cursor="pointer"
                  onClick={() => handleViewDetails(share)}
                  opacity={0.6}
                  _hover={{
                    bg: colorMode === 'dark' ? 'stone.700' : 'stone.200',
                    opacity: 0.8,
                  }}
                  transition="all 0.15s ease"
                >
                  <Tooltip label="Deleted">
                    <Icon
                      as={Trash2}
                      boxSize={4}
                      color="red.400"
                      flexShrink={0}
                    />
                  </Tooltip>
                  <Text
                    flex={1}
                    fontSize="sm"
                    noOfLines={1}
                    textDecoration="line-through"
                    color={colorMode === 'dark' ? 'stone.400' : 'stone.500'}
                  >
                    {getDisplayName(share)}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Collapse>
        </Box>
      )}
    </>
  )
}
